// The live preview of a meeting being recorded (BUILD 1 WP-4, C-4 meeting
// preview rules): the capture's frames go to the speech gateway over its
// WebSocket, and the provider's text comes back as greyed partials and final
// lines. It is best effort and never touches the recording: it sends live
// frames only (no replay), a lost socket reconnects with a new mint, and the
// time it was away is reported as "N s not shown live". The server persists
// the finals as provisional live rows (addendum A4); this link only shows
// them. Owner: WP-4.
import { SpeechHttpError, type PcmFrame, type SpeechSocketApi } from '@/speech/contracts';
import { CLOSE_CODES, RECONNECT_BACKOFF_MS, type ServerMessage } from '@/speech/transport/protocol';
import type { MeetingRecorderDeps } from './meeting-recorder-deps';

export type PreviewState = 'off' | 'connecting' | 'live' | 'paused' | 'unavailable';

/** A final line on the meeting timeline (take offset + take-relative time). */
export type PreviewLine = {
  key: string;
  text: string;
  atMs: number;
  endAtMs: number;
  speaker: string | null;
};
export type PreviewPartial = { key: string; text: string; stable: number; atMs: number };

export type PreviewEvents = {
  onState(state: PreviewState, reason: string | null): void;
  onPartial(partial: PreviewPartial | null): void;
  onFinal(line: PreviewLine): void;
  onNotShown(ms: number): void;
};

type PreviewDeps = Pick<
  MeetingRecorderDeps,
  'mint' | 'openSocket' | 'wsUrl' | 'now' | 'sleep' | 'random'
>;

/** Server errors after which the preview stays off for this take. */
const TERMINAL_ERRORS: ReadonlySet<string> = new Set([
  'provider_unavailable',
  'speech_unavailable',
  'budget_exhausted',
  'meeting_not_capturing',
  'not_authenticated',
  'company_assignment_required',
  'request_origin_not_allowed',
]);
const TERMINAL_CLOSES: ReadonlySet<number> = new Set([CLOSE_CODES.budget, CLOSE_CODES.protocol]);
const STOP_GRACE_MS = 1500;

const lineKey = (session: string, seg: number | string) => `${session}:${seg}`;

function mintFailure(error: unknown): { terminal: boolean; reason: string; waitMs: number | null } {
  if (!(error instanceof SpeechHttpError))
    return { terminal: false, reason: 'offline', waitMs: null };
  if (error.status === 429)
    return { terminal: false, reason: 'rate_limited', waitMs: (error.retryAfterSec ?? 5) * 1000 };
  // 402: the company's usage credit is used up (#2841). The recording itself
  // was admitted and continues; only its live preview stops.
  const terminal = [401, 402, 403, 503].includes(error.status);
  return { terminal, reason: error.reason ?? error.code, waitMs: null };
}

export type PreviewLink = ReturnType<typeof createPreviewLink>;

export function createPreviewLink(input: {
  deps: PreviewDeps;
  meetingId: string;
  offsetMs: number;
  events: PreviewEvents;
}) {
  const { deps, meetingId, events } = input;
  let offsetMs = input.offsetMs;
  let socket: SpeechSocketApi | null = null;
  let sessionId = '';
  let state: PreviewState = 'off';
  let closed = false;
  let attempt = 0;
  let lostAt: number | null = null;
  let generation = 0;
  /**
   * The last frame this session's socket accepted, or -1. `stop` names it:
   * naming a frame that was refused, or one sent to an earlier session, would
   * leave the server waiting for audio that never comes.
   */
  let lastSentSeq = -1;

  const setState = (next: PreviewState, reason: string | null = null) => {
    state = next;
    events.onState(next, reason);
  };

  function onMessage(message: ServerMessage) {
    if (message.type === 'ready') {
      attempt = 0;
      if (lostAt !== null) events.onNotShown(deps.now() - lostAt);
      lostAt = null;
      setState('live');
      return;
    }
    if (message.type === 'partial') {
      events.onPartial({
        key: lineKey(sessionId, message.seg),
        text: message.text,
        stable: message.stable,
        atMs: offsetMs + message.startMs,
      });
      return;
    }
    if (message.type === 'final') {
      events.onPartial(null);
      if (message.text.trim())
        events.onFinal({
          key: lineKey(sessionId, message.seg),
          text: message.text,
          atMs: offsetMs + message.startMs,
          endAtMs: offsetMs + message.endMs,
          speaker: message.speaker ?? null,
        });
      return;
    }
    if (message.type === 'error' && message.fatal && TERMINAL_ERRORS.has(message.code))
      stopTrying(message.code);
  }

  function stopTrying(reason: string) {
    closed = true;
    socket?.close();
    socket = null;
    setState('unavailable', reason);
  }

  function onClose(code: number, mine: number) {
    if (mine !== generation) return;
    socket = null;
    if (closed) return;
    if (TERMINAL_CLOSES.has(code)) {
      stopTrying(code === CLOSE_CODES.budget ? 'budget_exhausted' : 'protocol');
      return;
    }
    lostAt ??= deps.now();
    setState('paused');
    void reconnect();
  }

  async function reconnect() {
    const wait = RECONNECT_BACKOFF_MS[Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1)];
    attempt += 1;
    await deps.sleep(Math.round(wait * (0.75 + deps.random() / 2)));
    if (!closed && !socket) await connect();
  }

  async function connect() {
    const mine = ++generation;
    setState(state === 'paused' ? 'paused' : 'connecting');
    try {
      const minted = await deps.mint({ mode: 'meeting', meetingId });
      if (closed || mine !== generation) return;
      sessionId = minted.sessionId;
      lastSentSeq = -1;
      socket = deps.openSocket({
        url: `${deps.wsUrl(minted.wsPath)}?ticket=${encodeURIComponent(minted.ticket)}`,
        onMessage: (message) => {
          if (mine === generation) onMessage(message);
        },
        onClose: (code) => onClose(code, mine),
      });
    } catch (error) {
      if (closed || mine !== generation) return;
      const failure = mintFailure(error);
      if (failure.terminal) {
        stopTrying(failure.reason);
        return;
      }
      lostAt ??= deps.now();
      setState('paused', failure.reason);
      if (failure.waitMs) await deps.sleep(failure.waitMs);
      await reconnect();
    }
  }

  return {
    state: () => state,
    start: () => connect(),
    /** Live frames only: a frame the socket refuses is simply not shown. */
    sendFrame(frame: PcmFrame) {
      if (state !== 'live' || !socket) return;
      if (socket.sendFrame(frame)) lastSentSeq = frame.seq;
      else events.onNotShown(100);
    },
    setOffset(next: number) {
      offsetMs = next;
    },
    /** Let the provider finish the last words, then close. */
    async close() {
      if (closed) return;
      closed = true;
      const current = socket;
      socket = null;
      if (current) {
        current.send({ type: 'stop', lastSeq: lastSentSeq });
        await deps.sleep(STOP_GRACE_MS);
        current.close();
      }
      setState('off');
    },
  };
}
